export type CVTemplate = {
  id: string;
  name: string;
  tier: "free" | "premium";
  blurb: string;
  bestFor: string;
  avoidIf?: string; // who SHOULDN'T pick this template
  hireScore: number; // 0-100 ATS / interview probability
  accent: string; // tailwind class for accent color
  font: "sans" | "serif" | "mono";
  layout: "classic" | "split" | "minimal" | "executive" | "modern" | "creative";
  price?: number; // ZAR for premium (priced by quality + recruiter demand)
};

/**
 * Premium CV pricing model (ZAR) — capped at R30. No CV costs more than R30:
 *   Standard premium templates → R15
 *   Flagship / high-demand      → R30 (maximum)
 *
 * Free templates remain at R0. Only 2 free AI generations per browser.
 * Motivational letters are R50 (or R50 for the CV + letter bundle), and the
 * R25/month Career Unlimited plan covers everything with no per-item cost.
 */


export const CV_TEMPLATES: CVTemplate[] = [
  // ─────────────── FREE TEMPLATES (2 free AI generations per browser) ───────────────
  {
    id: "ats-clean",
    name: "ATS Clean",
    tier: "free",
    blurb:
      "Single-column, scanner-friendly layout. Passes most Applicant Tracking Systems used in SA.",
    bestFor: "Graduates, public sector, banking, corporates",
    hireScore: 92,
    accent: "bg-slate-700",
    font: "sans",
    layout: "classic",
  },
  {
    id: "modern-minimal",
    name: "Modern Minimal",
    tier: "free",
    blurb:
      "Clean typography with subtle accents. Great for tech, design and modern HR teams.",
    bestFor: "Tech, marketing, startups",
    hireScore: 87,
    accent: "bg-emerald-600",
    font: "sans",
    layout: "minimal",
  },
  {
    id: "graduate-launch",
    name: "Graduate Launch",
    tier: "free",
    blurb:
      "Optimised for new graduates with little experience. Leads with education, projects and skills first.",
    bestFor: "Graduates, interns, learnerships",
    hireScore: 89,
    accent: "bg-teal-600",
    font: "sans",
    layout: "minimal",
  },
  {
    id: "trades-skilled",
    name: "Skilled Trades",
    tier: "free",
    blurb:
      "Plain, foreman-friendly layout for artisans and operators. Tickets, licences and site experience first.",
    bestFor: "Artisans, drivers, operators, security",
    hireScore: 86,
    accent: "bg-stone-700",
    font: "sans",
    layout: "classic",
  },

  // ─────────────── PREMIUM — ENTRY TIER (R15) ───────────────
  {
    id: "starter-one-page",
    name: "Starter One-Page",
    tier: "premium",
    price: 15,
    blurb:
      "Polished single-page CV with a refined header and clean section dividers. Cheapest premium upgrade — great for first jobs.",
    bestFor: "Entry-level, part-time, retail, hospitality",
    hireScore: 85,
    accent: "bg-stone-600",
    font: "sans",
    layout: "minimal",
  },
  {
    id: "casual-worker",
    name: "Casual Worker",
    tier: "premium",
    price: 15,
    blurb:
      "Plain, friendly layout for casual, contract and gig work. Lists availability, transport and references clearly.",
    bestFor: "Casual labour, waiters, packers, drivers",
    hireScore: 84,
    accent: "bg-amber-700",
    font: "sans",
    layout: "classic",
  },
  {
    id: "school-leaver",
    name: "School Leaver (Matric)",
    tier: "premium",
    price: 15,
    blurb:
      "Designed for matriculants with no formal work experience. Leads with subjects, results and extracurriculars.",
    bestFor: "Matric leavers, gap-year applicants, learnerships",
    hireScore: 84,
    accent: "bg-teal-700",
    font: "sans",
    layout: "minimal",
  },

  // ─────────────── PREMIUM — R49 TIER ───────────────
  {
    id: "classic-pro-plus",
    name: "Classic Professional+",
    tier: "premium",
    price: 15,
    blurb:
      "Refined serif layout with subtle gold accents — conservative and authoritative for SA law and finance shortlists.",
    bestFor: "Legal, finance, academic, audit",
    hireScore: 88,
    accent: "bg-navy",
    font: "serif",
    layout: "classic",
  },
  {
    id: "hospitality-host",
    name: "Hospitality Host",
    tier: "premium",
    price: 15,
    blurb:
      "Warm, polished layout for tourism, hotels and front-of-house. Languages and service metrics elevated.",
    bestFor: "Hospitality, tourism, retail leadership",
    hireScore: 88,
    accent: "bg-rose-700",
    font: "serif",
    layout: "minimal",
  },
  {
    id: "creative-portfolio",
    name: "Creative Portfolio",
    tier: "premium",
    price: 15,
    blurb:
      "Header strip for portfolio links plus a compact case-study grid. Suited to designers, copywriters and content creators.",
    bestFor: "Designers, copywriters, content, video",
    hireScore: 87,
    accent: "bg-fuchsia-700",
    font: "sans",
    layout: "creative",
  },
  {
    id: "customer-service-star",
    name: "Customer Service Star",
    tier: "premium",
    price: 15,
    blurb:
      "CSAT, NPS, AHT and first-call-resolution at a glance. Tailored for contact centres and customer success.",
    bestFor: "Call centre agents, customer success, retentions",
    hireScore: 87,
    accent: "bg-teal-700",
    font: "sans",
    layout: "minimal",
  },

  // ─────────────── PREMIUM — R99 TIER ───────────────
  {
    id: "graduate-plus",
    name: "Graduate Plus",
    tier: "premium",
    price: 15,
    blurb:
      "Modern graduate template leading with NQF level, projects, internships and bursary work. Strong for first jobs and learnerships.",
    bestFor: "Recent graduates, learnerships, interns",
    hireScore: 90,
    accent: "bg-sky-600",
    font: "sans",
    layout: "minimal",
  },
  {
    id: "marketing-maven",
    name: "Marketing Maven",
    tier: "premium",
    price: 15,
    blurb:
      "Campaign-led layout with channels, KPIs and brand wins up front — built for digital, brand and growth marketers.",
    bestFor: "Marketing, brand, digital, growth, social media",
    hireScore: 90,
    accent: "bg-pink-600",
    font: "sans",
    layout: "modern",
  },
  {
    id: "logistics-supply-chain",
    name: "Logistics & Supply Chain",
    tier: "premium",
    price: 15,
    blurb:
      "Routes, fleet size, SLAs and savings up front. Suited to warehousing, transport, freight and SCM roles.",
    bestFor: "Supply chain, warehousing, transport, procurement",
    hireScore: 90,
    accent: "bg-amber-800",
    font: "sans",
    layout: "minimal",
  },
  {
    id: "education-mentor",
    name: "Education & Mentor",
    tier: "premium",
    price: 15,
    blurb:
      "Designed for SACE-registered educators, lecturers and trainers. Leads with subjects, phases and outcomes.",
    bestFor: "Teachers, lecturers, training",
    hireScore: 90,
    accent: "bg-violet-700",
    font: "serif",
    layout: "classic",
  },
  {
    id: "public-sector-z83",
    name: "Public Sector (Z83-ready)",
    tier: "premium",
    price: 15,
    blurb:
      "Layout aligned to the DPSA Z83 application: ID number, citizenship, qualifications by NQF level, and registration numbers up front.",
    bestFor: "Government, municipalities, SOEs, public service vacancies",
    avoidIf: "Avoid for private-sector tech, design or sales — feels too formal.",
    hireScore: 90,
    accent: "bg-navy",
    font: "serif",
    layout: "classic",
  },
  {
    id: "construction-site-lead",
    name: "Construction Site Lead",
    tier: "premium",
    price: 15,
    blurb:
      "CIDB grading, NHBRC, SACPCMP registration and site experience first. Safety record and tickets are featured.",
    bestFor: "Site agents, foremen, civil supervisors, QS",
    hireScore: 89,
    accent: "bg-yellow-700",
    font: "sans",
    layout: "classic",
  },
  {
    id: "it-support-specialist",
    name: "IT Support Specialist",
    tier: "premium",
    price: 15,
    blurb:
      "A+ / N+ / MCSE / ITIL certifications and ticket-resolution metrics first. Built for service desk and helpdesk roles.",
    bestFor: "IT support, service desk, sysadmin, MSP",
    hireScore: 89,
    accent: "bg-slate-700",
    font: "mono",
    layout: "modern",
  },

  // ─────────────── PREMIUM — R149 TIER ───────────────
  {
    id: "split-spotlight",
    name: "Split Spotlight",
    tier: "premium",
    price: 30,
    blurb:
      "Sidebar with skills and contact, main column for impact stories. Recruiter eye-tracking optimised.",
    bestFor: "Mid-career professionals, project managers",
    hireScore: 93,
    accent: "bg-indigo-700",
    font: "sans",
    layout: "split",
  },
  {
    id: "hr-people-ops",
    name: "HR & People Ops",
    tier: "premium",
    price: 30,
    blurb:
      "SABPP, EE/BBBEE, payroll and recruitment metrics front and centre. Built for HR generalists and business partners.",
    bestFor: "HR, recruitment, payroll, IR, L&D",
    hireScore: 91,
    accent: "bg-rose-700",
    font: "serif",
    layout: "classic",
  },
  {
    id: "sales-performance",
    name: "Sales Performance",
    tier: "premium",
    price: 30,
    blurb:
      "Quota-attainment scoreboard at the top, deal sizes and pipeline metrics anchor every role. Built for SA B2B/B2C sales managers.",
    bestFor: "Account executives, BD managers, sales leaders",
    avoidIf: "Avoid if you can't quantify results — empty numbers backfire.",
    hireScore: 92,
    accent: "bg-orange-600",
    font: "sans",
    layout: "split",
  },
  {
    id: "ngo-impact",
    name: "NGO & Impact",
    tier: "premium",
    price: 30,
    blurb:
      "Donor-friendly layout with programmes, beneficiaries reached and M&E framework — built for NGO, NPO and CSI roles.",
    bestFor: "NGO programme managers, M&E, fundraising, CSI",
    hireScore: 91,
    accent: "bg-green-700",
    font: "serif",
    layout: "classic",
  },
  {
    id: "creative-edge",
    name: "Creative Edge",
    tier: "premium",
    price: 30,
    blurb:
      "Bold header band and modern hierarchy. Stands out for creative and brand roles without losing ATS readability.",
    bestFor: "Design, media, communications",
    hireScore: 91,
    accent: "bg-rose-600",
    font: "sans",
    layout: "creative",
  },

  // ─────────────── PREMIUM — R199 TIER ───────────────
  {
    id: "tech-impact",
    name: "Tech Impact",
    tier: "premium",
    price: 30,
    blurb:
      "Engineered for software, data and product roles. Highlights stack, shipped projects and measurable impact.",
    bestFor: "Software engineers, data, product",
    hireScore: 94,
    accent: "bg-sky-700",
    font: "mono",
    layout: "modern",
  },
  {
    id: "finance-authority",
    name: "Finance Authority",
    tier: "premium",
    price: 30,
    blurb:
      "Conservative serif layout with quantified achievements. Built for SAICA, CFA and banking shortlists.",
    bestFor: "Auditors, analysts, CA(SA), banking",
    hireScore: 94,
    accent: "bg-slate-800",
    font: "serif",
    layout: "executive",
  },
  {
    id: "data-analyst-pro",
    name: "Data Analyst Pro",
    tier: "premium",
    price: 30,
    blurb:
      "Stack, tools and shipped dashboards lead. Sized for SA fintech, telco and consulting analytics teams.",
    bestFor: "Data analysts, BI, SQL/Python, reporting",
    hireScore: 93,
    accent: "bg-cyan-700",
    font: "mono",
    layout: "modern",
  },
  {
    id: "engineering-blueprint",
    name: "Engineering Blueprint",
    tier: "premium",
    price: 30,
    blurb:
      "Project-led layout for ECSA candidates and engineers. Highlights disciplines, standards and deliverables.",
    bestFor: "Civil, mechanical, electrical engineers",
    hireScore: 93,
    accent: "bg-zinc-800",
    font: "sans",
    layout: "modern",
  },
  {
    id: "mining-engineering",
    name: "Mining & Engineering",
    tier: "premium",
    price: 30,
    blurb:
      "Project-led with safety credentials, tickets and commodity experience first — built for MQA, ECSA and shaft-side hiring managers.",
    bestFor: "Mining engineers, mine surveyors, plant supervisors, artisans",
    avoidIf: "Avoid for office/admin roles — leads with on-site competencies.",
    hireScore: 93,
    accent: "bg-zinc-800",
    font: "sans",
    layout: "modern",
  },
  {
    id: "project-management-pro",
    name: "Project Management Pro",
    tier: "premium",
    price: 30,
    blurb:
      "PMBOK / Prince2 / Agile-friendly. Leads with budget, timeline, scope and delivered outcomes.",
    bestFor: "Project & programme managers, PMO, scrum masters",
    hireScore: 93,
    accent: "bg-indigo-800",
    font: "sans",
    layout: "split",
  },
  {
    id: "banking-insurance",
    name: "Banking & Insurance",
    tier: "premium",
    price: 30,
    blurb:
      "FAIS, FICA, RE5 and product accreditations lead. Compliance band sized for SA banks, insurers and FSPs.",
    bestFor: "Bankers, brokers, claims, underwriters, compliance",
    hireScore: 92,
    accent: "bg-blue-900",
    font: "serif",
    layout: "executive",
  },
  {
    id: "healthcare-hpcsa",
    name: "Healthcare Practitioner (HPCSA)",
    tier: "premium",
    price: 30,
    blurb:
      "HPCSA / SANC / SAPC registration band, scope of practice and rotation log first — formatted the way SA hospital recruiters scan.",
    bestFor: "Doctors, dentists, nurses, pharmacists, allied health",
    avoidIf: "Avoid for non-clinical roles — clinical-skills band looks out of place.",
    hireScore: 94,
    accent: "bg-emerald-700",
    font: "serif",
    layout: "split",
  },
  {
    id: "legal-counsel",
    name: "Legal Counsel",
    tier: "premium",
    price: 30,
    blurb:
      "Refined serif layout tuned for LPC, attorneys and in-house counsel. Matters, jurisdictions and admissions surface first.",
    bestFor: "Candidate attorneys, attorneys, legal advisors",
    hireScore: 94,
    accent: "bg-navy",
    font: "serif",
    layout: "executive",
  },

  // ─────────────── PREMIUM — R249 FLAGSHIP TIER ───────────────
  {
    id: "executive-edge",
    name: "Executive Edge",
    tier: "premium",
    price: 30,
    blurb:
      "Two-column flagship for senior leaders with leadership highlights, board exposure and a personal brand strip. Highest recruiter pull-through.",
    bestFor: "Managers, directors, C-suite",
    hireScore: 95,
    accent: "bg-amber-700",
    font: "serif",
    layout: "executive",
  },
  {
    id: "c-suite-portfolio",
    name: "C-Suite Portfolio",
    tier: "premium",
    price: 30,
    blurb:
      "Executive dossier with P&L scope, board appointments, strategic transformations and quantified shareholder impact. Premium binding-ready.",
    bestFor: "CEOs, MDs, CFOs, COOs, exec directors",
    hireScore: 96,
    accent: "bg-slate-900",
    font: "serif",
    layout: "executive",
  },
  {
    id: "medical-consultant-elite",
    name: "Medical Consultant Elite",
    tier: "premium",
    price: 30,
    blurb:
      "Specialist consultant layout — HPCSA specialist registration, sub-specialty, publications, M&M outcomes and teaching record at the top.",
    bestFor: "Specialists, registrars, consultants, academic clinicians",
    hireScore: 95,
    accent: "bg-emerald-800",
    font: "serif",
    layout: "executive",
  },
  {
    id: "judiciary-senior-counsel",
    name: "Judiciary & Senior Counsel",
    tier: "premium",
    price: 30,
    blurb:
      "Senior advocate / judge-track CV — admissions, reported judgments, silk applications, leadership in bar councils and CPD record.",
    bestFor: "Advocates, SC applicants, magistrates, judges",
    hireScore: 95,
    accent: "bg-navy",
    font: "serif",
    layout: "executive",
  },
];

export type CVData = {
  fullName: string;
  headline: string;
  email: string;
  phone: string;
  location: string;
  links?: { label: string; url: string }[];
  summary: string;
  skills: string[];
  experience: {
    role: string;
    company: string;
    location?: string;
    start: string;
    end: string;
    bullets: string[];
  }[];
  education: {
    qualification: string;
    institution: string;
    year: string;
    notes?: string;
  }[];
  certifications?: { name: string; issuer: string; year: string }[];
  achievements?: string[];
  awards?: { title: string; issuer?: string; year?: string; detail?: string }[];
  languages?: string[];
  references?: string;
};
